import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronRight } from 'lucide-react'
import type { KlasserRow } from '../Klasser/api'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { klasseDetaljerUrl } from '../detaljerUrl'

interface Props {
  rows: KlasserRow[]
}

type SortKey = 'class_label' | 'count' | 'share_pct' | 'snittalder_ar'
type SortConfig = { key: SortKey; direction: 'asc' | 'desc' } | null

const EM_DASH = '—'

const fmtInt = (v: number | null | undefined) =>
  v === null || v === undefined ? EM_DASH : v.toLocaleString('nb-NO')
const fmt1 = (v: number | null | undefined) =>
  v === null || v === undefined
    ? EM_DASH
    : v.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

export default function KlassefordelingGrid({ rows }: Props) {
  const { t } = useTranslation('bruktbil')
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'count', direction: 'desc' })
  const filters = useBruktbilFilters()
  const navigate = useNavigate()

  function toggleSort(key: SortKey) {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, direction: 'desc' }
      if (prev.direction === 'desc') return { key, direction: 'asc' }
      return null
    })
  }

  function sortIcon(key: SortKey) {
    if (sortConfig?.key !== key)
      return <ChevronsUpDown className="w-3.5 h-3.5 inline ml-1 opacity-40" />
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="w-3.5 h-3.5 inline ml-1" />
    ) : (
      <ArrowDown className="w-3.5 h-3.5 inline ml-1" />
    )
  }

  const sorted = useMemo(() => {
    if (!sortConfig) return rows
    const { key, direction } = sortConfig
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = a[key]
      const bv = b[key]
      if (av === null || av === undefined) return 1
      if (bv === null || bv === undefined) return -1
      if (typeof av === 'string' && typeof bv === 'string') {
        return direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      return direction === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
    return copy
  }, [rows, sortConfig])

  const totalCount = useMemo(() => rows.reduce((s, r) => s + r.count, 0), [rows])

  const columns: { key: SortKey; label: string; align: 'left' | 'right' }[] = [
    { key: 'class_label', label: t('common.klasse'), align: 'left' },
    { key: 'count', label: t('common.antall'), align: 'right' },
    { key: 'share_pct', label: t('common.andelPct'), align: 'right' },
    { key: 'snittalder_ar', label: t('oversikt.snittalderBilAar'), align: 'right' },
  ]

  if (rows.length === 0) {
    return (
      <div className="card p-6 text-sm text-gray-400 dark:text-gray-500">{t('common.ingenData')}</div>
    )
  }

  return (
    <div className="card p-6">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
        {t('oversikt.klassefordelingDetaljer')}
      </h3>
      <div className="overflow-x-auto">
        <table className="data-table data-table-compact w-full text-sm">
          <thead>
            <tr>
              {columns.map(c => (
                <th
                  key={c.key}
                  onClick={() => toggleSort(c.key)}
                  className={`cursor-pointer select-none ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {c.label}{sortIcon(c.key)}
                </th>
              ))}
              <th className="text-left">{t('oversikt.top3Merker')}</th>
              <th className="text-center">{t('oversikt.detaljer')}</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
              <td className="text-left">{t('common.sumTotal')}</td>
              <td className="text-right tabular-nums">{fmtInt(totalCount)}</td>
              <td colSpan={4} className="text-right text-gray-400 dark:text-gray-500" />
            </tr>
            {sorted.map((r, idx) => (
              <tr
                key={r.class_label}
                onClick={() => navigate(klasseDetaljerUrl(r.class_label, filters))}
                className={`cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-border transition-colors ${
                  idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''
                }`}
              >
                <td className="font-mono text-xs text-primary-600 dark:text-primary-400">
                  {r.class_label}
                </td>
                <td className="text-right tabular-nums">{fmtInt(r.count)}</td>
                <td className="text-right tabular-nums text-gray-500 dark:text-gray-400">
                  {fmt1(r.share_pct)}
                </td>
                <td className="text-right tabular-nums">{fmt1(r.snittalder_ar)}</td>
                <td className="text-left">
                  {r.top_merker && r.top_merker.length > 0
                    ? r.top_merker.join(', ')
                    : (r.top_merke ?? EM_DASH)}
                </td>
                <td className="text-center">
                  {/* The whole row navigates; this is the affordance that says so. */}
                  <button
                    onClick={e => { e.stopPropagation(); navigate(klasseDetaljerUrl(r.class_label, filters)) }}
                    className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <span className="text-xs font-medium">{t('common.seDetaljer')}</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
